/**
 * Save selected snapshots into another base as playEngine versions.
 *
 * Version names come from snapshot file names. The company choice decides
 * the base list, because a version has to land in a base the RECEIVING company
 * holds — its own users are the ones who will open it.
 *
 * The target list comes from the server and holds only playEngine bases that
 * company was granted — a version document means nothing anywhere else. The same
 * rules are re-applied when the export runs, so a stale list can only produce an
 * honest error, never an unauthorized write.
 */

import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListSubheader,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { exportSnapshotAsVersion, listVersionTargets } from "../../modules/snapshotEditorApi";
import { FIELD_LABEL, PANEL_BG } from "./panelPalette";

const useStyles = makeStyles({
  field: { marginTop: "12px" },
  hint: { fontSize: "12px", color: FIELD_LABEL, opacity: 0.8, marginTop: "10px", lineHeight: 1.6 },
  failure: { marginTop: "10px", fontSize: "12px", color: "#ef9a9a" },
  busy: { display: "flex", alignItems: "center", gap: "10px", marginTop: "12px" },
});

/** `beforeafter_01_02072026.json` → `beforeafter_01_02072026`. */
function defaultTitle(fileName) {
  return String(fileName || "").replace(/\.json$/i, "");
}

/**
 * Companies this user may export into, as reported by the session endpoint. Read
 * at render time rather than held in state: it changes only on reload.
 */
function readExportTargets() {
  const activeID = localStorage.getItem("user.companyID");
  const active = activeID
    ? [{ _id: activeID, name: localStorage.getItem("user.companyName") || "This company" }]
    : [];
  try {
    return _.uniqBy(
      [...active, ...JSON.parse(localStorage.getItem("user.exportTargets") || "[]")],
      (company) => String(company._id)
    );
  } catch (e) {
    return active;
  }
}

export default function DialogExportAsVersion({ editor, open, fileNames, onClose, onFileExported, onExported, createSnackbar }) {
  const classes = useStyles();
  const [targetBaseID, setTargetBaseID] = useState("");
  const [targetCompanyID, setTargetCompanyID] = useState(localStorage.getItem("user.companyID") || "");
  const exportTargets = useMemo(readExportTargets, [open]);
  const [targets, setTargets] = useState([]);
  const [areTargetsLoading, setAreTargetsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [completed, setCompleted] = useState(0);
  const [failure, setFailure] = useState(null);

  // Reset on each open: the dialog is mounted permanently by the snapshot list.
  useEffect(() => {
    if (!open) return;
    setTargetCompanyID(localStorage.getItem("user.companyID") || "");
    setFailure(null);
    setCompleted(0);
  }, [open]);

  useEffect(() => {
    if (!open || !editor.baseID) return;

    let isCurrent = true;
    setAreTargetsLoading(true);
    setTargetBaseID("");
    listVersionTargets(editor.baseID, { targetCompanyID })
      .then((response) => {
        if (!isCurrent) return;
        if (!response.ok) {
          setFailure(_.get(response, "error.error") || response.error);
          return;
        }
        setTargets(_.get(response, "data.targets", []));
      })
      .finally(() => {
        if (isCurrent) setAreTargetsLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [open, editor.baseID, targetCompanyID]);

  // Grouped by game, because base titles repeat across games ("Base 1", "Main")
  // and the game name is the half that tells them apart.
  const options = useMemo(() => {
    const rows = [];
    let lastGameID = null;
    for (const target of targets) {
      if (target.gameID !== lastGameID) {
        rows.push({ isHeader: true, key: `game-${target.gameID}`, label: target.gameTitle });
        lastGameID = target.gameID;
      }
      rows.push({ isHeader: false, key: target.baseID, label: target.title, value: target.baseID });
    }
    return rows;
  }, [targets]);

  const canSave = fileNames.length > 0 && Boolean(targetBaseID) && !isSaving && !areTargetsLoading;

  async function runExport() {
    if (!canSave) return;

    setIsSaving(true);
    setFailure(null);
    try {
      // Whatever is queued must reach the file first, or the version would carry
      // the snapshot on disk rather than the one on screen.
      await editor.flushSave();

      let completedCount = completed;
      for (const fileName of fileNames.slice(completed)) {
        const response = await exportSnapshotAsVersion(editor.baseID, {
          targetBaseID,
          targetCompanyID,
          fileName,
          title: defaultTitle(fileName),
        });

        if (!response.ok) {
          const code = _.get(response, "error.error") || response.error;
          const message = _.get(response, "error.message");
          const detail = message ? `${code}: ${message}` : code;
          setFailure(`${fileName}: ${detail}`);
          createSnackbar?.(`Export stopped after ${completedCount} versions: ${fileName}: ${detail}`, "error", false);
          return;
        }
        completedCount += 1;
        setCompleted((count) => count + 1);
        onFileExported?.(fileName);
      }

      createSnackbar?.(
        `${fileNames.length} version${fileNames.length === 1 ? "" : "s"} saved to the target base.`,
        "success",
        false
      );
      onExported?.();
      onClose();
    } catch (err) {
      setFailure(err.message || "Export failed");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ style: { backgroundColor: PANEL_BG } }}
    >
      <DialogTitle>Export as version</DialogTitle>
      <DialogContent>
        {exportTargets.length > 0 && (
          <TextField
            className={classes.field}
            label="Target company"
            value={targetCompanyID}
            onChange={(event) => setTargetCompanyID(event.target.value)}
            disabled={isSaving || completed > 0}
            select
            fullWidth
            helperText="The version, its assets and its design are all created in this company."
          >
            {exportTargets.map((company) => (
              <MenuItem key={company._id} value={String(company._id)}>
                {company.name}
              </MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          className={classes.field}
          label="Target base"
          value={targetBaseID}
          onChange={(event) => setTargetBaseID(event.target.value)}
          disabled={isSaving || areTargetsLoading || completed > 0}
          select
          fullWidth
          helperText={
            areTargetsLoading
              ? "Loading…"
              : targets.length === 0
                ? "No playEngine base available in your games."
                : " "
          }
        >
          {options.map((option) =>
            option.isHeader ? (
              <ListSubheader key={option.key} disableSticky>
                {option.label}
              </ListSubheader>
            ) : (
              <MenuItem key={option.key} value={option.value}>
                {option.label}
              </MenuItem>
            )
          )}
        </TextField>

        <Typography className={classes.hint}>
          A snapshot&apos;s inline assets are registered in the media library and the version is
          created in the target base. Assets already registered with identical content are reused
          rather than duplicated.
        </Typography>

        {isSaving && (
          <div className={classes.busy}>
            <CircularProgress size={18} />
            <Typography style={{ fontSize: "13px" }}>
              Exporting {completed + 1} of {fileNames.length}…
            </Typography>
          </div>
        )}

        {failure && <Typography className={classes.failure}>{failure}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Close
        </Button>
        <Button color="primary" variant="contained" disabled={!canSave} onClick={runExport}>
          Export
        </Button>
      </DialogActions>
    </Dialog>
  );
}
